import { PaginatedProjects } from "@custom-types/project-types";
import { useAppSelector } from "@store/store-helper";
import { searchSelector } from "@store/ui/ui-selector";
import { isValidProjectSearchQuery } from "@utils/project-utils";
import { useEffect, useState } from "react";

/** Custom hook to manage the projects search query */
export function useProjectsSearchQuery(): Pick<PaginatedProjects, "search" | "isDebouncingSearch"> {
  const { debouncedSearchText, isDebouncingSearch } = useAppSelector(searchSelector);
  const sanitizedDebouncedSearchText = debouncedSearchText.trim();
  const [search, setSearch] = useState<string | undefined>(undefined);

  // Caches the search query value
  // Only updates the search state if the debounced search text is valid or cleared
  useEffect(() => {
    if (isDebouncingSearch || search === sanitizedDebouncedSearchText) {
      return;
    }

    if (isValidProjectSearchQuery(sanitizedDebouncedSearchText)) {
      return setSearch(sanitizedDebouncedSearchText);
    }

    if (sanitizedDebouncedSearchText.length === 0) {
      return setSearch(undefined);
    }
  }, [isDebouncingSearch, sanitizedDebouncedSearchText, search]);

  return {
    search,
    isDebouncingSearch: isDebouncingSearch && isValidProjectSearchQuery(debouncedSearchText),
  };
}
